import React, { useEffect, useState, useCallback } from 'react';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdRefresh,
  MdCategory, MdWork, MdBusiness, MdAccountBalance, MdFolder
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import { jobMasterApi } from '../../../api/jobManagementApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './JobMasterData.css';

const TABS = [
  { id: 'verticals', label: 'Verticals', icon: MdCategory },
  { id: 'specialties', label: 'Specialties', icon: MdWork },
  { id: 'designations', label: 'Designations', icon: MdBusiness },
  { id: 'projects', label: 'Projects', icon: MdFolder },
  { id: 'costCenters', label: 'Cost Centers', icon: MdAccountBalance }
];

function JobMasterData() {
  const [activeTab, setActiveTab] = useState('verticals');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdowns for dependent entities
  const [verticals, setVerticals] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({});

  // Load verticals for dropdown (used by specialties and projects)
  const loadVerticals = async () => {
    try {
      const response = await jobMasterApi.getVerticalsDropdown();
      if (response.success) {
        setVerticals(response.data || []);
      }
    } catch (e) {
      console.error('Failed to load verticals:', e);
    }
  };

  // Load data based on active tab
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'verticals':
          response = await jobMasterApi.getVerticals();
          break;
        case 'specialties':
          response = await jobMasterApi.getSpecialties();
          break;
        case 'designations':
          response = await jobMasterApi.getDesignations();
          break;
        case 'projects':
          response = await jobMasterApi.getProjects();
          break;
        case 'costCenters':
          response = await jobMasterApi.getCostCenters();
          break;
        default:
          response = { success: true, data: [] };
      }

      if (response.success) {
        const items = response.data?.content || response.data || [];
        setData(Array.isArray(items) ? items : []);
      }
    } catch (e) {
      console.error(`Failed to load ${activeTab}:`, e);
      toast.error(`Failed to load ${activeTab}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
    loadVerticals();
  }, [loadData]);

  // Filter data based on search
  const filteredData = data.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    const name = item.name || item.verticalName || item.specialtyName || item.designationName || '';
    const code = item.code || item.verticalCode || '';
    return name.toLowerCase().includes(searchLower) || code.toLowerCase().includes(searchLower);
  });

  // Get form fields based on active tab
  const getFormFields = () => {
    switch (activeTab) {
      case 'verticals':
        return [
          { name: 'verticalCode', label: 'Vertical Code', type: 'text', required: true, placeholder: 'e.g., HIM' },
          { name: 'verticalName', label: 'Vertical Name', type: 'text', required: true, placeholder: 'e.g., Health Information Management' },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
        ];
      case 'specialties':
        return [
          { name: 'verticalId', label: 'Vertical', type: 'select', required: true, options: verticals.map(v => ({ value: v.id, label: v.name })) },
          { name: 'specialtyCode', label: 'Specialty Code', type: 'text', required: true, placeholder: 'e.g., ICD-10' },
          { name: 'specialtyName', label: 'Specialty Name', type: 'text', required: true, placeholder: 'e.g., ICD-10 Coding' },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
        ];
      case 'designations':
        return [
          { name: 'designationCode', label: 'Designation Code', type: 'text', required: true, placeholder: 'e.g., MC-SR' },
          { name: 'designationName', label: 'Designation Name', type: 'text', required: true, placeholder: 'e.g., Senior Medical Coder' },
          { name: 'level', label: 'Level', type: 'number', placeholder: 'e.g., 3' },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
        ];
      case 'projects':
        return [
          { name: 'verticalId', label: 'Vertical', type: 'select', required: true, options: verticals.map(v => ({ value: v.id, label: v.name })) },
          { name: 'projectCode', label: 'Project Code', type: 'text', required: true, placeholder: 'e.g., PRJ-001' },
          { name: 'projectName', label: 'Project Name', type: 'text', required: true, placeholder: 'e.g., Client ABC Project' },
          { name: 'clientName', label: 'Client Name', type: 'text', placeholder: 'e.g., ABC Healthcare' },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
        ];
      case 'costCenters':
        return [
          { name: 'costCenterCode', label: 'Cost Center Code', type: 'text', required: true, placeholder: 'e.g., CC-001' },
          { name: 'costCenterName', label: 'Cost Center Name', type: 'text', required: true, placeholder: 'e.g., Operations' },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
        ];
      default:
        return [];
    }
  };

  // Get table columns based on active tab
  const getTableColumns = () => {
    switch (activeTab) {
      case 'verticals':
        return ['Code', 'Name', 'Description', 'Status', 'Actions'];
      case 'specialties':
        return ['Vertical', 'Code', 'Name', 'Description', 'Status', 'Actions'];
      case 'designations':
        return ['Code', 'Name', 'Level', 'Description', 'Status', 'Actions'];
      case 'projects':
        return ['Vertical', 'Code', 'Name', 'Client', 'Status', 'Actions'];
      case 'costCenters':
        return ['Code', 'Name', 'Description', 'Status', 'Actions'];
      default:
        return [];
    }
  };

  // Render table row based on active tab
  const renderTableRow = (item) => {
    const getStatusBadge = (status) => (
      <span className={`status-badge ${status === 'ACTIVE' ? 'active' : 'inactive'}`}>
        {status || 'ACTIVE'}
      </span>
    );

    switch (activeTab) {
      case 'verticals':
        return (
          <>
            <div className="cell">{item.verticalCode || item.code || '-'}</div>
            <div className="cell">{item.verticalName || item.name || '-'}</div>
            <div className="cell">{item.description || '-'}</div>
            <div className="cell">{getStatusBadge(item.status)}</div>
          </>
        );
      case 'specialties':
        return (
          <>
            <div className="cell">{item.vertical?.verticalName || item.verticalName || '-'}</div>
            <div className="cell">{item.specialtyCode || item.code || '-'}</div>
            <div className="cell">{item.specialtyName || item.name || '-'}</div>
            <div className="cell">{item.description || '-'}</div>
            <div className="cell">{getStatusBadge(item.status)}</div>
          </>
        );
      case 'designations':
        return (
          <>
            <div className="cell">{item.designationCode || item.code || '-'}</div>
            <div className="cell">{item.designationName || item.name || '-'}</div>
            <div className="cell">{item.level || '-'}</div>
            <div className="cell">{item.description || '-'}</div>
            <div className="cell">{getStatusBadge(item.status)}</div>
          </>
        );
      case 'projects':
        return (
          <>
            <div className="cell">{item.vertical?.verticalName || item.verticalName || '-'}</div>
            <div className="cell">{item.projectCode || item.code || '-'}</div>
            <div className="cell">{item.projectName || item.name || '-'}</div>
            <div className="cell">{item.clientName || '-'}</div>
            <div className="cell">{getStatusBadge(item.status)}</div>
          </>
        );
      case 'costCenters':
        return (
          <>
            <div className="cell">{item.costCenterCode || item.code || '-'}</div>
            <div className="cell">{item.costCenterName || item.name || '-'}</div>
            <div className="cell">{item.description || '-'}</div>
            <div className="cell">{getStatusBadge(item.status)}</div>
          </>
        );
      default:
        return null;
    }
  };

  // Open add modal
  const openAddModal = () => {
    setEditingItem(null);
    setFormData({ status: 'ACTIVE' });
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      verticalId: item.vertical?.id || item.verticalId || ''
    });
    setShowModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let response;
      const payload = { ...formData };

      // Handle vertical reference for dependent entities
      if (payload.verticalId && (activeTab === 'specialties' || activeTab === 'projects')) {
        payload.vertical = { id: payload.verticalId };
      }

      if (editingItem) {
        // Update
        switch (activeTab) {
          case 'verticals':
            response = await jobMasterApi.updateVertical(editingItem.id, payload);
            break;
          case 'specialties':
            response = await jobMasterApi.updateSpecialty(editingItem.id, payload);
            break;
          case 'designations':
            response = await jobMasterApi.updateDesignation(editingItem.id, payload);
            break;
          case 'projects':
            response = await jobMasterApi.updateProject(editingItem.id, payload);
            break;
          case 'costCenters':
            response = await jobMasterApi.updateCostCenter(editingItem.id, payload);
            break;
          default:
            break;
        }
        toast.success('Updated successfully');
      } else {
        // Create
        switch (activeTab) {
          case 'verticals':
            response = await jobMasterApi.createVertical(payload);
            break;
          case 'specialties':
            response = await jobMasterApi.createSpecialty(payload);
            break;
          case 'designations':
            response = await jobMasterApi.createDesignation(payload);
            break;
          case 'projects':
            response = await jobMasterApi.createProject(payload);
            break;
          case 'costCenters':
            response = await jobMasterApi.createCostCenter(payload);
            break;
          default:
            break;
        }
        toast.success('Created successfully');
      }

      if (response?.success) {
        await loadData();
        if (activeTab === 'verticals') {
          await loadVerticals();
        }
        setShowModal(false);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Operation failed';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      switch (activeTab) {
        case 'verticals':
          await jobMasterApi.deleteVertical(deleteConfirm.id);
          break;
        case 'specialties':
          await jobMasterApi.deleteSpecialty(deleteConfirm.id);
          break;
        case 'designations':
          await jobMasterApi.deleteDesignation(deleteConfirm.id);
          break;
        case 'projects':
          await jobMasterApi.deleteProject(deleteConfirm.id);
          break;
        case 'costCenters':
          await jobMasterApi.deleteCostCenter(deleteConfirm.id);
          break;
        default:
          break;
      }
      toast.success('Deleted successfully');
      await loadData();
      if (activeTab === 'verticals') {
        await loadVerticals();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getTabLabel = () => {
    const tab = TABS.find(t => t.id === activeTab);
    return tab ? tab.label : '';
  };

  const getItemName = (item) => {
    return item.verticalName || item.specialtyName || item.designationName ||
           item.projectName || item.costCenterName || item.name || 'this item';
  };

  return (
    <div className="admin-view job-master-data-view">
      {/* Header with search and add button - At Top */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdCategory className="title-icon" />
            Master Data
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder={`Search ${getTabLabel().toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn-book-compact" onClick={loadData} title="Refresh">
            <MdRefresh />
          </button>
          <button className="btn-book-compact" onClick={openAddModal}>
            <MdAdd />
            <span>Add {getTabLabel().slice(0, -1)}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
          >
            <tab.icon className="tab-icon" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="table card">
        <div className="table-head">
          {getTableColumns().map((col, idx) => (
            <div key={idx} className="cell" style={col === 'Actions' ? { textAlign: 'right' } : {}}>
              {col}
            </div>
          ))}
        </div>

        {isLoading && Array.from({ length: 5 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isLoading && filteredData.map((item) => (
          <div className="table-row" key={item.id}>
            {renderTableRow(item)}
            <div className="cell" style={{ textAlign: 'right', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
              <button className="btn-action-compact btn-edit" onClick={() => openEditModal(item)} title="Edit">
                <MdEdit />
              </button>
              <button className="btn-action-compact btn-delete" onClick={() => setDeleteConfirm(item)} title="Delete">
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {!isLoading && filteredData.length === 0 && (
          <div className="empty-state">
            <h3>No {getTabLabel()} Found</h3>
            <p>Click "Add {getTabLabel().slice(0, -1)}" to create one</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingItem ? `Edit ${getTabLabel().slice(0, -1)}` : `Add ${getTabLabel().slice(0, -1)}`}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>x</button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              {getFormFields().map(field => (
                <div className="form-group" key={field.name}>
                  <label htmlFor={field.name}>
                    {field.label} {field.required && '*'}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      placeholder={field.placeholder}
                      rows="3"
                    />
                  ) : (
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      required={field.required}
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))}

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status || 'ACTIVE'}
                  onChange={handleInputChange}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>x</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{getItemName(deleteConfirm)}</strong>?</p>
              <p style={{ color: '#dc2626', fontSize: '13px' }}>This action cannot be undone.</p>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobMasterData;
